/** Shared helpers for the messages inbox UI. */

import { formatRelativeTime, formatShortDate } from "./dates"

/** Short calendar date — e.g. "16 Aug". */
export function formatMessageDate(ms: number): string {
  return formatShortDate(ms)
}

/** Relative time for thread list secondary line — e.g. "3d ago". */
export function formatMessageRelative(ms: number): string {
  return formatRelativeTime(ms)
}

function sameLocalDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/** Day divider label for message feeds — e.g. "Today", "Yesterday". */
export function formatMessageDayLabel(ms: number): string {
  const date = new Date(ms)
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  if (sameLocalDay(date, today)) return "Today"
  if (sameLocalDay(date, yesterday)) return "Yesterday"
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  }).format(date)
}

export function messageDayKey(ms: number): string {
  const d = new Date(ms)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

export type MessageFilter = "all" | "unread" | "jobs"

/**
 * What the browser sends per attachment: a finalizable upload claim and a
 * display name. MIME/size are read server-side from storage metadata.
 */
export type MessageAttachmentInput = {
  claimId: import("../../convex/_generated/dataModel").Id<"assetClaims">
  storageId: import("../../convex/_generated/dataModel").Id<"_storage">
  fileName: string
}

export const MAX_MESSAGE_ATTACHMENTS = 5
export const MAX_MESSAGE_ATTACHMENT_BYTES = 10 * 1024 * 1024

const ALLOWED_ATTACHMENT_PREFIXES = ["image/"]
const ALLOWED_ATTACHMENT_TYPES = new Set([
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
])

export function isImageAttachment(contentType: string) {
  return contentType.toLowerCase().startsWith("image/")
}

export function formatAttachmentSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function validateMessageAttachmentFile(file: File) {
  if (file.size > MAX_MESSAGE_ATTACHMENT_BYTES) {
    throw new Error(`${file.name} is too large (max 10 MB)`)
  }
  const contentType = (file.type || "application/octet-stream").toLowerCase()
  const allowed =
    ALLOWED_ATTACHMENT_PREFIXES.some((prefix) => contentType.startsWith(prefix)) ||
    ALLOWED_ATTACHMENT_TYPES.has(contentType)
  if (!allowed) {
    throw new Error(`${file.name} is not a supported file type`)
  }
}
